import React, { useEffect } from 'react';

import { useStore } from 'react-redux';

import getConfig from 'next/config';

import { withBonnetApp } from '@bonnet/next/app';
import { withCtxMiddleware } from '@bonnet/next/ctx-middleware';
import { setReduxDefaultMiddlewareOptions } from '@bonnet/next/redux';

import atcId from '@atc/bonnet-ctx-atc-id';
import withBrand from '@atc/bonnet-ctx-brand';
import parseSeo from '@atc/bonnet-ctx-parse-seo';
import addPxaId from '@atc/bonnet-ctx-pxa-id';
import userLocation from '@atc/bonnet-ctx-user-location';
import { BrandProvider, useBrand } from '@atc/brand';
import { Provider as FeaturesProvider } from '@atc/features';
import { addOptimizelyDynamicConfig, OptimizelyPageProvider } from '@atc/optimizely-toolkit';

import UserProvider from '@kbbsdk/global-sdk/navigation/web/mykbb/UserProvider';

import { ReaxlConfigurationContext } from 'reaxl';

import reaxlConfiguration from '@/config/reaxlConfiguration';

import addAtcReqId from '@/context-middleware/addAtcReqId.js';
import getConfigs from '@/context-middleware/getConfigs.js';
import withCPOcontent from '@/context-middleware/withCpoContent.js';
import withFeatures from '@/context-middleware/withFeatures.js';
import withKBBPageData from '@/context-middleware/withKBBPageData.js';
import { withResetOptimizelyExperiments } from '@/context-middleware/withOptimizelyExperiments';
import withSeoDecodedPath from '@/context-middleware/withSeoDecodedPath.js';
import withUseFeatures from '@/context-middleware/withUseFeatures.js';

import activeInteractionDuck from '@/ducks/activeInteractionDuck.js';
import atcIdDuck from '@/ducks/atcIdDuck.js';
import authDuck from '@/ducks/authDuck.js';
import birfDuck from '@/ducks/birfDuck.js';
import brandDuck from '@/ducks/brandDuck.js';
import cbhVehicleInterestsDuck from '@/ducks/cbhVehicleInterestsDuck.js';
import compareListingsDuck from '@/ducks/compareListingsDuck.js';
import consumerBrowsingHistoryDuck from '@/ducks/consumerBrowsingHistoryDuck.js';
import consumerRatingsDuck from '@/ducks/consumerRatingsDuck.js';
import cpoContentDuck from '@/ducks/cpoContentDuck.js';
import currentPageNameDuck from '@/ducks/currentPageNameDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import eLotResultsDuck from '@/ducks/eLotResultsDuck.js';
import experimentDuck from '@/ducks/experimentDuck.js';
import globalConfigsDuck from '@/ducks/globalConfigsDuck.js';
import interestRateDuck from '@/ducks/interestRateDuck.js';
import inventoryDuck from '@/ducks/inventoryDuck.js';
import kbbPageDataDuck from '@/ducks/kbbPageDataDuck.js';
import kbbVRSDataDuck from '@/ducks/kbbVRSDataDuck.js';
import ownersDuck from '@/ducks/ownersDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import preorderDuck from '@/ducks/preorderDuck.js';
import preorderOwnersDuck from '@/ducks/preorderOwnersDuck.js';
import recentSearchDuck from '@/ducks/recentSearchDuck.js';
import requestParamsDuck from '@/ducks/requestParamsDuck.js';
import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';
import shareEmailSubmissionDuck from '@/ducks/shareEmailSubmissionDuck.js';
import shareTextSubmissionDuck from '@/ducks/shareTextSubmissionDuck.js';
import suggestedSearchesDuck from '@/ducks/suggestedSearchesDuck.js';
import userAgentDuck from '@/ducks/userAgentDuck.js';
import userDuck from '@/ducks/userDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';

import useExperiments from '@/hooks/useExperiments';

import onNewRelicAlert from '@/newrelic/onNewRelicAlert';
import useNewRelicClientConfiguration from '@/newrelic/useNewRelicClientConfiguration';

import ThemeLoader from '@/theme/ThemeLoader';

import BaseContainer from '@/containers/BaseContainer';
import FooterContainer from '@/containers/FooterContainer';
import OptimizelyEdgeContainer from '@/containers/global/OptimizelyEdgeContainer';
import OneTapSignInSuppression from '@/containers/OneTapSignInSuppression';
import OptimizelyAnalyticsListenerContainer from '@/containers/OptimizelyAnalyticsListenerContainer';
import PendoScriptContainer from '@/containers/PendoScriptContainer';
import ScreenReaderContainer from '@/containers/ScreenReaderContainer';
import ScrollRestoration from '@/containers/ScrollRestoration';

import AppFooter from '@/components/AppFooter';
import AppHeader from '@/components/AppHeader';
import WebVitalsReporter from '@/components/WebVitalsReporter';

if (typeof window !== 'undefined') {

    if (!global.thirdPartyDeferPromise) {
        global.thirdPartyDeferPromise = new Promise((res) => {
            if (document?.readyState === 'complete') {
                res();
            } else {
                window.addEventListener('load', res, { once: true });
            }
        });
    }
}

// Disable redux middleware performance checks
// TODO: Re-enable and fix these issues!
setReduxDefaultMiddlewareOptions({
    immutableCheck: false,
    serializableCheck: false,
});

function ConditionalProvider({ brand, brands, children }) {
    return brands.KBB_BRAND === brand ? <UserProvider>{children}</UserProvider> : children;
}

function App(props) {
    const {
        ATC_REQID,
        Component,
        featuresConfig = {},
        brand = 'atc',
        optimizelyDynamicConfig = {},
        pageProps,
    } = props;

    useEffect(() => {
        window.history.scrollRestoration = 'manual';
    }, []);

    // support Cypress Redux tests
    const store = useStore();
    if (typeof window !== 'undefined' && window.Cypress) {
        window.store = store;
    }
    const enableWebVitals = pageProps?.query?.enableWebVitals;

    useNewRelicClientConfiguration({ reqId: ATC_REQID });

    const { listenerConfig } = useExperiments();

    const { brands } = useBrand();

    return (
        <ReaxlConfigurationContext.Provider value={reaxlConfiguration}>
            <WebVitalsReporter enabled={enableWebVitals} />

            <FeaturesProvider {...featuresConfig}>
                <OptimizelyPageProvider
                    disabled={process.env.DISABLE_OPTIMIZELY}
                    optimizelyDynamicConfig={optimizelyDynamicConfig}
                    listenerConfig={listenerConfig}
                >
                    <BrandProvider brand={brand}>
                        <ThemeLoader />
                        <OptimizelyAnalyticsListenerContainer />
                        <OptimizelyEdgeContainer />
                        <ScreenReaderContainer />
                        <OneTapSignInSuppression />
                        <ConditionalProvider
                            brand={brand}
                            brands={brands}
                        >
                            <AppHeader />
                            <Component {...pageProps} />
                        </ConditionalProvider>
                        <AppFooter />
                        <FooterContainer isBot={pageProps.isBot} />
                        <BaseContainer thirdPartyScripts={{}} />
                        <ScrollRestoration />
                        <PendoScriptContainer />
                    </BrandProvider>
                </OptimizelyPageProvider>
            </FeaturesProvider>
        </ReaxlConfigurationContext.Provider>
    );
}

App.getInitialProps = async (appCtx) => {
    const { ctx } = appCtx;
    const { publicRuntimeConfig = {} } = getConfig();
    const { environment } = publicRuntimeConfig;

    // apply context middleware
    await withCtxMiddleware([
        withSeoDecodedPath(),
        parseSeo(),
        withBrand(),
        atcId(),
        // make sure this happens before addOptimizelyDynamicConfig
        userLocation({
            // disable client-side geolocation
            useGeolocation: false,
            userDuck,
        }),
        // enable optimizely configuration
        addOptimizelyDynamicConfig({ environment, onConfigLoadError: onNewRelicAlert }),
        withResetOptimizelyExperiments(),
        // create features configuration
        withFeatures({ environment }),
        // enable usage of features/optimizely in ctx-middleware
        withUseFeatures(),
        addPxaId(),
        addAtcReqId(),
        getConfigs(),
        withCPOcontent(),
        withKBBPageData(),
    ], ctx);

    // NEXTJS CLIENT APP RENDER FIXES
    // nextjs _app getInitialProps is run on each client page load unlike previous bonnet versions
    // Any ctx-middleware that runs on the server only and and sets data that is then passed down as a prop
    // to the _app component needs to be remedied to work on client runs

    // client page loads are missing the optimizelyDynamicConfig data
    // only push this to redux if a value is present to prevent
    // undefined values from being pushed to state
    if (ctx.data.ATC_ID) {
        ctx.store.dispatch(atcIdDuck.creators.setString(ctx.data.ATC_ID));
    }

    return {
        brand: ctx.data.brand,
        isBot: ctx.data.isBot,
        environment,
        featuresConfig: ctx.data.featuresConfig,
        optimizelyDynamicConfig: ctx.data.optimizelyDynamicConfig,
        ATC_ID: ctx.data.ATC_ID,
        ATC_REQID: ctx.data.ATC_REQID,
    };
};

const { BonnetAppComponent } = withBonnetApp(App, {
    reducers: [
        atcIdDuck,
        authDuck,
        birfDuck,
        brandDuck,
        userPreferencesDuck,
        consumerBrowsingHistoryDuck,
        cbhVehicleInterestsDuck,
        compareListingsDuck,
        consumerRatingsDuck,
        cpoContentDuck,
        currentPageNameDuck,
        dealsDuck,
        eLotResultsDuck,
        experimentDuck,
        globalConfigsDuck,
        inventoryDuck,
        interestRateDuck,
        kbbPageDataDuck,
        kbbVRSDataDuck,
        ownersDuck,
        paymentsDuck,
        preorderDuck,
        preorderOwnersDuck,
        recentSearchDuck,
        requestParamsDuck,
        savedInventoryDuck,
        shareEmailSubmissionDuck,
        shareTextSubmissionDuck,
        suggestedSearchesDuck,
        userAgentDuck,
        userDuck,
        activeInteractionDuck,
    ],
});

export default BonnetAppComponent;
